import type { CharacterDraft, ProjectInput } from '../shared/types';
import type { ImageGenerationErrorResponse } from '../shared/types';
import type { ImageModelId } from '../shared/image-models';
import type { AudioGeneration, AudioSegment, SceneAudioManifest } from '../shared/audio';
import type { ProjectAudioConfig } from '../shared/voices';
import type { EffectiveFeatures, FeaturePreference, GenerationAccess } from '../shared/account';
import type { StoryLength } from '../shared/story-length';
import { DEFAULT_TEXT_MODEL, type TextModelId } from '../shared/text-models';
import type { UsagePage } from '../shared/billing';
import type { SceneEditMode } from '../shared/scene-editing';
import { getTurnstileToken, protectedTurnstileAction } from './turnstile';
import { pollGenerationTask, resolveGenerationProject, type ClientGenerationTask, type GenerationTaskPollerOptions } from './generation-task';

export type ClientScene = {
  index: number;
  title: string;
  summary: string;
  text: string;
  imagePrompt?: string;
};

export type SceneEditRequest = {
  mode: SceneEditMode;
  selectedParagraphs: number[];
  sceneText: string;
  instruction: string;
  targetCharacters?: number;
  length?: StoryLength;
  model?: TextModelId;
};

export type ClientProject = {
  id: string;
  title: string;
  summary: string;
  characters: Array<ClientCharacter>;
  audioConfig: ProjectAudioConfig;
  input: ProjectInput;
  scenes: ClientScene[];
  status: string;
  createdAt: string;
  updatedAt: string;
};

export type ClientProjectSummary = {
  id: string;
  title: string;
  status: string;
  sceneCount: number;
  createdAt: string;
  updatedAt: string;
};

export type ClientProjectPage = {
  projects: ClientProjectSummary[];
  nextCursor?: string;
};

export type ClientProjectTransferInventory = {
  sceneCount: number;
  imageCount: number;
  imageBytes: number;
  audioSceneCount: number;
  audioSegmentCount: number;
  audioBytes: number;
};

export type ClientProjectTransferJob = {
  id: string;
  type: 'export' | 'import';
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'expired';
  progress: number;
  options: { images: boolean; audio: boolean; textFormat?: 'structured' | 'source' };
  preview?: { title?: string; sceneCount?: number; characterCount?: number; imageCount?: number; audioSceneCount?: number; projectId?: string };
  warnings: string[];
  errorCode?: string;
  errorMessage?: string;
};

export type ClientCharacter = CharacterDraft;

export type AuthConfig = {
  phoneLogin: boolean;
  turnstileSiteKey: string;
  turnstileRequired: boolean;
};

export type AuthMeBase = {
  role: 'user' | 'admin';
  plan: { key: string; name: string };
  balanceMicros: string;
  balanceText: string;
  preferences: FeaturePreference;
  features: EffectiveFeatures;
  generationAccess: GenerationAccess;
  storyLengths?: StoryLength[];
};

export type AuthMe =
  | (AuthMeBase & { authenticated: false })
  | (AuthMeBase & { authenticated: true; provider: 'phone'; maskedPhone: string; nickname: string; bio: string });

export const DEFAULT_GUEST_ACCOUNT: Extract<AuthMe, { authenticated: false }> = {
  authenticated: false,
  role: 'user',
  plan: { key: 'beta', name: '游客用户' },
  balanceMicros: '0',
  balanceText: '¥0.00',
  storyLengths: ['short'],
  preferences: { image: true, audio: true, video: false },
  features: {
    image: { available: true, allowed: true, enabled: true },
    audio: { available: true, allowed: true, enabled: true },
    video: { available: false, allowed: false, enabled: false, reason: '即将上线' },
  },
  generationAccess: {
    guestTrialAvailable: true,
    requiresLogin: false,
    requiresBalance: false,
    adminContact: '',
  },
};

export type ClientReferenceAsset = {
  id: string;
  projectId: string;
  label: string;
  sha256: string;
  mimeType: string;
  byteSize: number;
  storageKey: string;
  createdAt: string;
  url: string;
};

export type ImageJob = {
  id: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  progress: number;
  assets: Array<{ sceneIndex: number; url: string; prompt: string }>;
  error?: string;
};

export type SceneImageSource = 'prompt' | 'excerpt' | 'scene-edit' | 'custom';
export type SceneImageModel = ImageModelId;

export type SceneImage = {
  id: string;
  projectId: string;
  sceneIndex: number;
  provider: string;
  model?: SceneImageModel;
  prompt: string;
  storageKey: string;
  mimeType: string;
  width: number;
  height: number;
  createdAt: string;
  isPrimary: boolean;
  url: string;
};

export type ClientAudioGeneration = AudioGeneration;
export type ClientAudioSegment = AudioSegment;
export type ClientSceneAudioManifest = SceneAudioManifest;

export type ApiErrorPayload = Partial<Omit<ImageGenerationErrorResponse, 'code'>> & {
  code?: string;
  error?: string;
  [key: string]: unknown;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly payload: ApiErrorPayload;
  readonly error?: string;
  readonly suggestion?: string;
  readonly requestId?: string;
  readonly retryable?: boolean;

  constructor(status: number, payload: ApiErrorPayload) {
    const error = typeof payload.error === 'string' ? payload.error : undefined;
    const message = typeof payload.message === 'string' ? payload.message : error;
    super(message || '请求失败');
    this.name = 'ApiError';
    this.status = status;
    this.code = typeof payload.code === 'string' ? payload.code : undefined;
    this.payload = payload;
    this.error = error;
    this.suggestion = typeof payload.suggestion === 'string' ? payload.suggestion : undefined;
    this.requestId = typeof payload.requestId === 'string' ? payload.requestId : undefined;
    this.retryable = typeof payload.retryable === 'boolean' ? payload.retryable : undefined;
  }
}

export const DEFAULT_API_REQUEST_TIMEOUT_MS = 45_000;
export const DEFAULT_GENERATION_API_REQUEST_TIMEOUT_MS = 120_000;

export function isProjectNotFoundError(error: unknown): error is ApiError {
  return error instanceof ApiError
    && error.status === 404
    && error.code === 'PROJECT_NOT_FOUND';
}

function fallbackHttpError(status: number): ApiErrorPayload {
  if (status >= 500) {
    return {
      code: 'SERVER_ERROR',
      error: `服务器暂时不可用（HTTP ${status}），请稍后重试。`,
    };
  }
  return {
    code: 'HTTP_ERROR',
    error: `请求失败（HTTP ${status}）。`,
  };
}

async function request<T>(path: string, init?: RequestInit, timeoutMs = DEFAULT_API_REQUEST_TIMEOUT_MS): Promise<T> {
  const method = (init?.method || 'GET').toUpperCase();
  const action = protectedTurnstileAction(path, method);
  const turnstileToken = action ? await getTurnstileToken(action) : '';
  const hasExternalSignal = Boolean(init?.signal);
  const controller = hasExternalSignal ? undefined : new AbortController();
  let timedOut = false;
  const timeout = controller ? globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs) : undefined;
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      ...(controller ? { signal: controller.signal } : {}),
      headers: {
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...(turnstileToken ? { 'x-turnstile-token': turnstileToken } : {}),
        ...init?.headers,
      },
    });
  } catch (error) {
    if (timedOut) {
      throw new ApiError(504, { code: 'REQUEST_TIMEOUT', error: '请求处理超时，请刷新后查看最新内容。', retryable: true });
    }
    throw error;
  } finally {
    if (timeout !== undefined) globalThis.clearTimeout(timeout);
  }
  const rawBody = await response.text();
  let body: unknown;
  if (rawBody.trim()) {
    try {
      body = JSON.parse(rawBody) as unknown;
    } catch {
      if (!response.ok) {
        throw new ApiError(response.status, fallbackHttpError(response.status));
      }
      throw new ApiError(502, {
        code: 'INVALID_RESPONSE',
        error: '服务器返回了无法识别的响应，请稍后重试。',
      });
    }
  }
  if (!response.ok) {
    const payload = body && typeof body === 'object'
      ? body as ApiErrorPayload
      : fallbackHttpError(response.status);
    throw new ApiError(response.status, payload);
  }
  return body as T;
}

export async function getAuthConfig() {
  return request<AuthConfig>('/api/auth/config');
}

export async function getAuthMe() {
  return request<AuthMe>('/api/auth/me');
}

export async function sendPhoneCode(phone: string) {
  return request<{ challengeId: string; expiresAt: string }>('/api/auth/phone/send', {
    method: 'POST',
    body: JSON.stringify({ phone: phone.trim() }),
  });
}

export async function verifyPhoneCode(challengeId: string, code: string) {
  return request<Extract<AuthMe, { authenticated: true }>>('/api/auth/phone/verify', {
    method: 'POST',
    body: JSON.stringify({ challengeId, code: code.trim() }),
  });
}

export async function logout() {
  await request<void>('/api/auth/logout', { method: 'POST' });
}

export async function updateProfile(nickname: string, bio: string) {
  return request<Extract<AuthMe, { authenticated: true }>>('/api/auth/profile', {
    method: 'PATCH',
    body: JSON.stringify({ nickname: nickname.trim(), bio: bio.trim() }),
  });
}

export async function updateAccountSettings(
  nickname: string,
  bio: string,
  preferences: FeaturePreference,
) {
  return request<Extract<AuthMe, { authenticated: true }>>('/api/account/settings', {
    method: 'PATCH',
    body: JSON.stringify({
      nickname: nickname.trim(),
      bio: bio.trim(),
      preferences,
    }),
  });
}

export async function getAccountUsage(cursor?: string, limit = 20, signal?: AbortSignal) {
  const path = `/api/account/usage?limit=${limit}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
  return request<UsagePage>(path, { signal });
}

export async function listProjects(cursor?: string, limit = 20, signal?: AbortSignal) {
  const path = `/api/projects?limit=${limit}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
  return request<ClientProjectPage>(path, { signal });
}

export async function getProject(projectId: string, signal?: AbortSignal) {
  const response = await request<{ project: ClientProject }>(`/api/projects/${projectId}`, { signal });
  return response.project;
}

export async function getProjectTransferInventory(projectId: string, signal?: AbortSignal) {
  return request<ClientProjectTransferInventory>(`/api/projects/${projectId}/transfer-inventory`, { signal });
}

export async function createProjectExport(projectId: string, options: { images: boolean; audio: boolean; textFormat?: 'structured' | 'source' }, idempotencyKey = crypto.randomUUID()) {
  if (!options.images && !options.audio) {
    const response = await fetch(`/api/projects/${projectId}/exports`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...options, idempotencyKey }),
    });
    if (!response.ok) throw new ApiError(response.status, await response.json() as ApiErrorPayload);
    return { kind: 'text' as const, blob: await response.blob() };
  }
  const response = await request<{ job: ClientProjectTransferJob }>(`/api/projects/${projectId}/exports`, {
    method: 'POST',
    body: JSON.stringify({ ...options, idempotencyKey }),
  });
  return { kind: 'job' as const, job: response.job };
}

export async function getProjectTransferJob(transferId: string, signal?: AbortSignal) {
  const response = await request<{ job: ClientProjectTransferJob }>(`/api/project-transfers/${transferId}`, { signal });
  return response.job;
}

export async function downloadProjectTransfer(transferId: string) {
  const response = await fetch(`/api/project-transfers/${transferId}/download`);
  if (!response.ok) throw new ApiError(response.status, await response.json() as ApiErrorPayload);
  return response.blob();
}

export async function uploadProjectImport(file: File, idempotencyKey = crypto.randomUUID()) {
  const form = new FormData();
  form.append('file', file);
  form.append('idempotencyKey', idempotencyKey);
  const response = await fetch('/api/project-imports', { method: 'POST', body: form });
  const body = await response.json() as { job: ClientProjectTransferJob; preview: ClientProjectTransferJob['preview'] } | ApiErrorPayload;
  if (!response.ok) throw new ApiError(response.status, body as ApiErrorPayload);
  return body as { job: ClientProjectTransferJob; preview: ClientProjectTransferJob['preview'] };
}

export async function confirmProjectImport(transferId: string) {
  const response = await request<{ job: ClientProjectTransferJob }>(`/api/project-imports/${transferId}/confirm`, { method: 'POST' });
  return response.job;
}

export async function getGenerationTask(taskId: string, signal?: AbortSignal) {
  const response = await request<{ task: ClientGenerationTask }>(`/api/generation-tasks/${taskId}`, { signal });
  return response.task;
}

export async function listActiveGenerationTasks(signal?: AbortSignal) {
  const response = await request<{ tasks: ClientGenerationTask[] }>('/api/generation-tasks?status=active', { signal });
  return response.tasks;
}

async function resolveProjectResponse(
  response: { project?: ClientProject; taskId?: string },
  options?: GenerationTaskPollerOptions,
) {
  return resolveGenerationProject(response, getGenerationTask, getProject, options);
}

export async function createProject(input: ProjectInput, model: TextModelId = DEFAULT_TEXT_MODEL, options?: GenerationTaskPollerOptions) {
  const response = await request<{ project?: ClientProject; taskId?: string }>('/api/projects', {
    method: 'POST',
    body: JSON.stringify({ ...input, model, idempotencyKey: crypto.randomUUID() }),
  }, DEFAULT_GENERATION_API_REQUEST_TIMEOUT_MS);
  return resolveProjectResponse(response, options);
}

export async function updateProject(projectId: string, title: string, summary: string) {
  const response = await request<{ project: ClientProject }>(`/api/projects/${projectId}`, {
    method: 'PATCH',
    body: JSON.stringify({ title: title.trim(), summary: summary.trim() }),
  });
  return response.project;
}

export async function updateProjectAudioConfig(projectId: string, audioConfig: ProjectAudioConfig) {
  const response = await request<{ project: ClientProject }>(`/api/projects/${projectId}/audio-config`, {
    method: 'PATCH',
    body: JSON.stringify(audioConfig),
  });
  return response.project;
}

export function getVoicePreviewUrl(voice: string) {
  return `/api/audio/voices/${encodeURIComponent(voice)}/preview`;
}

export async function updateCharacter(projectId: string, characterIndex: number, character: Omit<CharacterDraft, 'imageAssetId'>) {
  const response = await request<{ project: ClientProject }>(`/api/projects/${projectId}/characters/${characterIndex}`, {
    method: 'PATCH',
    body: JSON.stringify(character),
  });
  return response.project;
}

export async function addCharacter(projectId: string, character: Omit<CharacterDraft, 'imageAssetId'>) {
  const response = await request<{ project: ClientProject }>(`/api/projects/${projectId}/characters`, {
    method: 'POST',
    body: JSON.stringify(character),
  });
  return response.project;
}

export async function deleteCharacter(projectId: string, characterIndex: number) {
  const response = await request<{ project: ClientProject }>(`/api/projects/${projectId}/characters/${characterIndex}`, {
    method: 'DELETE',
  });
  return response.project;
}

export async function generateImages(projectId: string) {
  const response = await request<{ job: ImageJob }>(`/api/projects/${projectId}/images`, {
    method: 'POST',
    body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }),
  });
  return response.job;
}

export function getSceneImageUrl(projectId: string, sceneIndex: number, cacheKey?: string) {
  const path = `/api/projects/${projectId}/scenes/${sceneIndex}/image`;
  return cacheKey ? `${path}?v=${encodeURIComponent(cacheKey)}` : path;
}

export function getSceneImageVersionUrl(projectId: string, sceneIndex: number, assetId: string) {
  return `/api/projects/${projectId}/scenes/${sceneIndex}/images/${assetId}`;
}

export async function listSceneImages(projectId: string, sceneIndex: number, signal?: AbortSignal) {
  const response = await request<{ assets: SceneImage[] }>(`/api/projects/${projectId}/scenes/${sceneIndex}/images`, { signal });
  return response.assets;
}

export async function setSceneImagePrimary(projectId: string, sceneIndex: number, assetId: string, signal?: AbortSignal) {
  const response = await request<{ asset: SceneImage }>(`/api/projects/${projectId}/scenes/${sceneIndex}/images/${assetId}/primary`, {
    method: 'PATCH',
    signal,
  });
  return response.asset;
}

export async function deleteSceneImage(projectId: string, sceneIndex: number, assetId: string, signal?: AbortSignal) {
  const response = await request<{ asset: SceneImage | null }>(`/api/projects/${projectId}/scenes/${sceneIndex}/images/${assetId}`, {
    method: 'DELETE',
    signal,
  });
  return response.asset;
}

export function getReferenceAssetUrl(projectId: string, assetId: string) {
  return `/api/projects/${projectId}/reference-assets/${assetId}`;
}

export async function listReferenceAssets(projectId: string, signal?: AbortSignal) {
  const response = await request<{ assets: ClientReferenceAsset[] }>(`/api/projects/${projectId}/reference-assets`, { signal });
  return response.assets;
}

export async function uploadReferenceAsset(projectId: string, dataUrl: string, label?: string) {
  const response = await request<{ asset: ClientReferenceAsset }>(`/api/projects/${projectId}/reference-assets`, {
    method: 'POST',
    body: JSON.stringify({ dataUrl, ...(label?.trim() ? { label: label.trim() } : {}) }),
  });
  return response.asset;
}

export async function deleteReferenceAsset(projectId: string, assetId: string) {
  await request<void>(`/api/projects/${projectId}/reference-assets/${assetId}`, { method: 'DELETE' });
}

export async function bindCharacterImage(projectId: string, characterIndex: number, assetId: string | null) {
  const response = await request<{ project: ClientProject }>(`/api/projects/${projectId}/characters/${characterIndex}/reference-image`, {
    method: 'PATCH',
    body: JSON.stringify({ assetId }),
  });
  return response.project;
}

export async function rewriteScene(
  projectId: string,
  sceneIndex: number,
  instruction: string,
  length?: StoryLength,
  model: TextModelId = DEFAULT_TEXT_MODEL,
  options?: GenerationTaskPollerOptions,
) {
  const response = await request<{ project?: ClientProject; taskId?: string }>(`/api/projects/${projectId}/scenes/${sceneIndex}/rewrite`, {
    method: 'POST',
    body: JSON.stringify({ instruction: instruction.trim(), ...(length ? { length } : {}), model, idempotencyKey: crypto.randomUUID() }),
  }, DEFAULT_GENERATION_API_REQUEST_TIMEOUT_MS);
  return resolveProjectResponse(response, options);
}

async function sceneTextHash(text: string) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, '0')).join('');
}

export async function editScene(
  projectId: string,
  sceneIndex: number,
  input: SceneEditRequest,
  options?: GenerationTaskPollerOptions,
) {
  const response = await request<{ project?: ClientProject; taskId?: string }>(`/api/projects/${projectId}/scenes/${sceneIndex}/rewrite`, {
    method: 'POST',
    body: JSON.stringify({
      mode: input.mode,
      selectedParagraphs: input.selectedParagraphs,
      sceneTextHash: await sceneTextHash(input.sceneText),
      instruction: input.instruction.trim(),
      ...(input.targetCharacters ? { targetCharacters: input.targetCharacters } : {}),
      ...(input.length ? { length: input.length } : {}),
      model: input.model || DEFAULT_TEXT_MODEL,
      idempotencyKey: crypto.randomUUID(),
    }),
  }, DEFAULT_GENERATION_API_REQUEST_TIMEOUT_MS);
  return resolveProjectResponse(response, options);
}

export async function generateSceneImage(projectId: string, sceneIndex: number, source: SceneImageSource, model: SceneImageModel, customPrompt?: string, referenceImageDataUrl?: string, referenceAssetIds?: string[], signal?: AbortSignal, pollOptions?: GenerationTaskPollerOptions) {
  const response = await request<{ asset?: SceneImage; task?: ClientGenerationTask }>(`/api/projects/${projectId}/scenes/${sceneIndex}/image`, {
    method: 'POST',
    body: JSON.stringify({
      source,
      model,
      idempotencyKey: crypto.randomUUID(),
      ...(customPrompt?.trim() ? { customPrompt: customPrompt.trim() } : {}),
      ...(referenceAssetIds?.length ? { referenceAssetIds } : {}),
      ...(referenceImageDataUrl ? { referenceImage: { dataUrl: referenceImageDataUrl } } : {}),
    }),
    signal,
  });
  if (response.asset) return response.asset;
  if (!response.task) throw new ApiError(502, { code: 'IMAGE_TASK_RESULT_MISSING', error: '图片任务没有返回结果，请稍后重试。' });
  pollOptions?.onProgress?.(response.task);
  const finished = await pollGenerationTask(response.task.id, getGenerationTask, {
    ...pollOptions,
    signal: pollOptions?.signal ?? signal,
  });
  const resultAssetId = typeof finished.resultRef?.assetId === 'string' ? finished.resultRef.assetId : undefined;
  const assets = await listSceneImages(projectId, sceneIndex, pollOptions?.signal ?? signal);
  const asset = resultAssetId ? assets.find((item) => item.id === resultAssetId) : assets.find((item) => item.isPrimary);
  if (!asset) throw new ApiError(502, { code: 'IMAGE_TASK_RESULT_MISSING', error: '图片已经生成，但暂时无法读取素材，请刷新后重试。' });
  return asset;
}

export async function continueProject(
  projectId: string,
  length: StoryLength,
  instruction?: string,
  model: TextModelId = DEFAULT_TEXT_MODEL,
  options?: GenerationTaskPollerOptions,
) {
  const response = await request<{ project?: ClientProject; taskId?: string }>(`/api/projects/${projectId}/continue`, {
    method: 'POST',
    body: JSON.stringify({ length, model, idempotencyKey: crypto.randomUUID(), ...(instruction ? { instruction } : {}) }),
  }, DEFAULT_GENERATION_API_REQUEST_TIMEOUT_MS);
  return resolveProjectResponse(response, options);
}

export async function deleteScenesFrom(projectId: string, sceneIndex: number) {
  const response = await request<{ project: ClientProject }>(`/api/projects/${projectId}/scenes/${sceneIndex}`, {
    method: 'DELETE',
  });
  return response.project;
}

export async function deleteProject(projectId: string) {
  await request<void>(`/api/projects/${projectId}`, { method: 'DELETE' });
}

export async function getJob(jobId: string) {
  const response = await request<{ job: ImageJob }>(`/api/jobs/${jobId}`);
  return response.job;
}

export async function generateSceneAudio(projectId: string, sceneIndex: number, signal?: AbortSignal) {
  const response = await request<{ generation: ClientAudioGeneration }>(`/api/projects/${projectId}/scenes/${sceneIndex}/audio`, {
    method: 'POST',
    body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }),
    signal,
  });
  return response.generation;
}

export async function getSceneAudio(projectId: string, sceneIndex: number, signal?: AbortSignal) {
  return request<ClientSceneAudioManifest>(`/api/projects/${projectId}/scenes/${sceneIndex}/audio`, { signal });
}
